import { isCameraStreamLogsEnabled } from '../config';
import { kurentoClientManager } from './KurentoClient/KurentoClientManager';

export class BaseCameraStream {
    async getMediaObjectById(id) {
        const mediaObjectType = this.#getMediaObjectType(id);
        try {
            this.log(`getMediaObjectById(): getting ${mediaObjectType}Id=${id}`);
            this.kurentoClient = await kurentoClientManager.get(this.kurentoId);
            const mediaObject = await this.kurentoClient.getMediaobjectById(id, this.#callback);
            this.log(`getMediaObjectById(): returning ${mediaObjectType}`);
            return mediaObject;
        } catch (err) {
            this.handle(err, `getMediaObjectById(): id=${id}`, err.cause || `${mediaObjectType} not found`);
        }
    }

    async addTagToMediaObject(mediaObject, key, value) {
        try {
            if (!mediaObject) throw new Error('No MediaObject');
            await mediaObject.addTag(key, value, this.#callback);
            this.log(`addTagToMediaObject(): Added tag ${key}=${value}`);
        } catch (err) {
            const id = mediaObject?.id;
            this.handle(err, `addTagToMediaObject(): id=${id}`, `Adding tag to ${this.#getMediaObjectType(id)} failed`);
        }
    }

    async getMediaObjectTags(mediaObject) {
        try {
            if (!mediaObject) throw new Error('No MediaObject');
            return await mediaObject.getTags(this.#callback);
        } catch (err) {
            const id = mediaObject?.id;
            this.handle(err, `getMediaObjectTags(): id=${id}`, `Getting tags for ${this.#getMediaObjectType(id)} failed`);
        }
    }

    async getMediaObjectCreationTime(mediaObject) {
        try {
            if (!mediaObject) throw new Error('No MediaObject');
            return await mediaObject.getCreationTime(this.#callback);
        } catch (err) {
            const id = mediaObject?.id;
            this.handle(err, `getMediaObjectCreationTime(): id=${id}`, `Getting ${this.#getMediaObjectType(id)} creation time failed`);
        }
    }

    async configureMediaObject(mediaObject, action, value) {
        const id = mediaObject?.id;
        const mediaObjectType = this.#getMediaObjectType(id);
        try {
            if (!mediaObject) throw new Error('No MediaObject');
            this.log(`configureMediaObject():Setting ${action}=${value} on ${mediaObjectType} id=${id}`);
            await mediaObject[action](value, this.#callback);
        } catch (err) {
            this.handle(err, `configureMediaObject(): action=${action} id=${id} `, `Configuring ${mediaObjectType} failed`);
        }
    }

    async connectMediaElements(source, sink, action = 'connect', padType = 'VIDEO') {
        try {
            if (!source) throw new Error('No Source Element');
            if (!sink) throw new Error('No Sink Element');
            const callback = action === 'connect' ? this.#callback : () => {};
            await source[action](sink, padType, callback);
        } catch (err) {
            this.handle(err, `connectMediaElements():`, `Connecting media elements failed`);
        }
    }

    //mediaPipeline
    async createMediaPipelineElement(mediaPipeline, mediaElement = 'WebRtcEndpoint', retryCount = 0) {
        try {
            const element = await mediaPipeline.create(mediaElement);

            if (retryCount)
                console.log(
                    `${this.fullHeader} createMediaPipelineElement(): WebRtcEndpoint successfully created after retry, mediaPipelineId=${mediaPipeline?.id}, retryCount=${retryCount}`
                );
            return element;
        } catch (err) {
            const raceConditionMessage = 'Cannot register media object, it was not created by MediaSet';
            if (err.message === raceConditionMessage && retryCount < 3) {
                const randomWaitFor = Math.floor(Math.random() * (600 - 100 + 1)) + 100;
                const retry = retryCount + 1;
                this.log(
                    `createMediaPipelineElement(): Detected race condition on WebRtcEndpoint creation in mediaPipelineId=${mediaPipeline?.id}, scheduling retry in ${randomWaitFor}, retryCount=${retry}`
                );
                await new Promise((res) => setTimeout(res, randomWaitFor));
                return await this.createMediaPipelineElement(mediaPipeline, mediaElement, retry);
            }
            if (err.message === raceConditionMessage)
                console.log(
                    `${this.fullHeader} createMediaPipelineElement(): Avoiding race condition unsuccessful, mediaPipeline=${mediaPipeline}, retryCount=${retryCount}`
                );
            this.handle(err, `createMediaPipelineElement()`, `Error creating MediaPipeline element`);
        }
    }

    //webRTCEndpoint

    #getMediaObjectType(mediaObjectId) {
        return mediaObjectId?.split('.')?.at(-1);
    }

    #callback(err, result) {
        if (err) throw err;
        return result;
    }

    log(message, mode = 'log') {
        if (!isCameraStreamLogsEnabled(mode)) return;
        console[mode](`${this.fullHeader} ${message}`);
    }

    handle(err, message, rethrow = true) {
        if (err) {
            this.log(`${message}: ${err.message ?? err}`, 'error'); // log real error so we can use it do debug in logs, and render our custom error
            if (rethrow) {
                const rethrowMessage = typeof rethrow === 'string' ? rethrow : (err?.message ?? err);
                throw new Error(rethrowMessage);
            }
        }
    }

    updateKurentoId(id) {
        if (id === this.kurentoId) return;
        this.log(`updateKurentoId(): Updating kurentoId, old id=${this.kurentoId}, new id=${id}`);
        this.kurentoId = id;
        this.updateLogHeader();
        return true;
    }
}
